import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SupabaseClient } from "@supabase/supabase-js";
import { toast } from "@/components/ui/use-toast";
import { getUserTimezone } from "@/data/timezones";
import { AuthContextType, User, DEMO_USER } from "../types/auth-types";
import { supabase } from "@/integrations/supabase/client";
import { 
  mapUserProfile, 
  storeDemoUser, 
  removeDemoUser,
  getDemoUserFromStorage,
  testSupabaseConnection
} from "../utils/auth-utils";

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
  login: async () => {},
  signup: async () => {},
  logout: () => {},
  resetPassword: async () => {},
  upgradeAccount: async () => {},
  supabase: supabase
});

// Initialize Supabase client from the imported client
console.log("AuthProvider initializing with Supabase");

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log("Checking for active session...");
        
        // Check for Supabase session first
        console.log("Checking Supabase session...");
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session error:", sessionError);
          setLoading(false);
          return;
        }
        
        // If we have an active Supabase session, use that and ignore any demo user
        if (session) {
          console.log("Found active session for user:", session.user.id);
          // Make sure to clean up any stored demo user
          removeDemoUser();
          
          const { data: userData, error: userError } = await supabase
            .from('ft_profiles')  // Updated from 'profiles' to 'ft_profiles'
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (userError) {
            console.error("Error fetching user profile:", userError);
            setUser(null);
          } else if (userData) {
            console.log("User profile fetched successfully:", userData);
            setUser(mapUserProfile(session.user.id, session.user.email as string, userData));
          }
        } else {
          // Only if we don't have an active session, check for demo user
          console.log("No active session found, checking for demo user");
          const demoUser = getDemoUserFromStorage();
          if (demoUser) {
            console.log("Found demo user in localStorage:", demoUser.email);
            // Update timezone in case it's changed
            demoUser.timezone = demoUser.timezone || getUserTimezone();
            setUser(demoUser);
          } else {
            console.log("No demo user found either");
          }
        }
      } catch (error) {
        console.error("Session check error:", error);
      } finally {
        setLoading(false);
      }
    };
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed:", event, session?.user?.id);
        
        if (event === "SIGNED_IN" && session) {
          // On sign in, remove any demo user
          removeDemoUser();
          
          const { data: userData, error: userError } = await supabase
            .from('ft_profiles')  // Updated from 'profiles' to 'ft_profiles'
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (!userError && userData) {
            setUser(mapUserProfile(session.user.id, session.user.email as string, userData));
          } else {
            console.error("Error loading profile after sign in:", userError);
            // Try to create a profile if one doesn't exist
            try {
              const { error: insertError } = await supabase
                .from('ft_profiles')  // Updated from 'profiles' to 'ft_profiles'
                .insert({
                  id: session.user.id,
                  email: session.user.email,  // Added email field as it's required
                  first_name: session.user?.user_metadata?.first_name || '',
                  last_name: session.user?.user_metadata?.last_name || '',
                  gender: session.user?.user_metadata?.gender || 'male',
                  timezone: session.user?.user_metadata?.timezone || getUserTimezone(),
                  plan: 'free'
                });
                
              if (insertError) {
                console.error("Error creating user profile:", insertError);
              } else {
                // Try fetching again
                const { data: newUserData } = await supabase
                  .from('ft_profiles')  // Updated from 'profiles' to 'ft_profiles'
                  .select('*')
                  .eq('id', session.user.id)
                  .single();
                  
                if (newUserData) {
                  setUser(mapUserProfile(session.user.id, session.user.email as string, newUserData));
                }
              }
            } catch (err) {
              console.error("Error in profile creation:", err);
            }
          }
        } else if (event === "SIGNED_OUT") {
          setUser(null);
          removeDemoUser();
        }
        setLoading(false);
      }
    );
    
    checkSession();
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log("Login function called with email:", email);
      
      // Handle demo user login directly without Supabase
      if (email.toLowerCase() === "demo@example.com" && password === "password") {
        console.log("Demo login detected - setting demo user without Supabase auth");
        // Ensure demo user has current timezone
        const demoUserWithTimezone = {
          ...DEMO_USER,
          timezone: getUserTimezone()
        };
        // Set the demo user state to enable app access
        setUser(demoUserWithTimezone);
        // Store in localStorage for persistence across refreshes
        storeDemoUser(demoUserWithTimezone);
        setLoading(false);
        return;
      }
      
      // Normal Supabase login for other users
      console.log("Normal login - authenticating with Supabase");
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error("Supabase login error:", error);
        throw new Error(error.message);
      }
      
      if (data.user) {
        console.log("User authenticated successfully:", data.user.id);
        // Remove any demo user that might be in storage
        removeDemoUser();
        
        const { data: userData, error: userError } = await supabase
          .from('ft_profiles')  // Updated from 'profiles' to 'ft_profiles'
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (userError) {
          console.error("Error fetching user profile:", userError);
          
          // Try to create a profile if one doesn't exist
          try {
            const { error: insertError } = await supabase
              .from('ft_profiles')  // Updated from 'profiles' to 'ft_profiles'
              .insert({
                id: data.user.id,
                email: data.user.email,  // Added email field as it's required
                first_name: data.user?.user_metadata?.first_name || '',
                last_name: data.user?.user_metadata?.last_name || '',
                gender: data.user?.user_metadata?.gender || 'male',
                timezone: getUserTimezone(),
                plan: 'free'
              });
              
            if (insertError) {
              console.error("Error creating user profile:", insertError);
            } else {
              // Try fetching again
              const { data: newUserData } = await supabase
                .from('ft_profiles')  // Updated from 'profiles' to 'ft_profiles'
                .select('*')
                .eq('id', data.user.id)
                .single();
                
              if (newUserData) {
                setUser(mapUserProfile(data.user.id, data.user.email as string, newUserData));
              }
            }
          } catch (err) {
            console.error("Error in profile creation:", err);
          }
        } else if (userData) {
          console.log("User profile fetched successfully");
          setUser(mapUserProfile(data.user.id, data.user.email as string, userData));
        }
      }
    } catch (err: any) {
      setError(err.message || "Login failed");
      console.error("Login error:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const signup = async (email: string, password: string, firstName: string, lastName: string, gender?: "male" | "female") => {
    setLoading(true);
    setError(null);
    
    try {
      console.log("Starting signup process with:", { email, firstName, lastName, gender });
      
      localStorage.setItem('signupEmail', email);
      
      // Step 1: Create auth user
      console.log("Creating auth user with Supabase...");
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            gender: gender || "male",
            timezone: getUserTimezone()
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        console.error("Signup auth error from Supabase:", error);
        throw error;
      }
      
      if (!data.user) {
        throw new Error("Signup failed - no user returned");
      }
      
      console.log("Auth user created successfully:", {
        userId: data.user.id,
        email: data.user.email
      });
      
      // Profile will be created automatically via the database trigger
      console.log("Profile will be created via database trigger");
      
      if (data.session) {
        console.log("User session created - no email confirmation required");
        setUser({
          id: data.user.id,
          email: data.user.email as string,
          firstName: firstName,
          lastName: lastName,
          gender: gender,
          timezone: getUserTimezone(),
          plan: 'free'
        });
        navigate("/dashboard");
      } else {
        console.log("Email confirmation required - redirecting to signup-success");
        navigate("/signup-success");
      }
    } catch (err: any) {
      setError(err.message || "Signup failed");
      console.error("Signup error:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const upgradeAccount = async () => {
    if (!user) return;
    
    try {
      // Check if profiles table has a plan column
      // If not, we'll handle the upgrade differently
      setUser({
        ...user,
        plan: 'pro'
      });
      
      toast({
        title: "Account upgraded!",
        description: "You now have access to all Pro features.",
      });
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Upgrade failed",
        description: err.message || "Could not upgrade account.",
      });
    }
  };
  
  const resetPassword = async (email: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        throw new Error(error.message);
      }
    } catch (err: any) {
      setError(err.message || "Password reset failed");
      console.error("Password reset error:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const logout = async () => {
    setLoading(true);
    
    try {
      // Always clear the demo user from localStorage
      removeDemoUser();

      // Only attempt to sign out from Supabase if we're not a demo user
      if (user && user.id !== "demo-user-id") {
        await supabase.auth.signOut();
      }
      
      setUser(null);
      navigate("/");
    } catch (err: any) {
      setError(err.message || "Logout failed");
      console.error("Logout error:", err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        error,
        login,
        signup,
        logout,
        resetPassword,
        upgradeAccount,
        supabase
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
